import {
  Job,
  JobDetails,
  JobFailure,
  JobListWithCount,
  JobMode,
  JobObject,
  JobObjectFailure,
  JobObjectFailuresWithCount,
  JobObjectListWithCount,
  JobSessionLogTaskResponse,
  JobStatus
} from './interfaces/job';
import {
  GenericStatusRawData,
  JobDetailsRawData,
  JobListRawData,
  JobObjectFailureRawData,
  JobObjectFailuresRawData,
  JobObjectRawData,
  JobObjectsRawData,
  JobRawData,
  JobRawFailures,
  JobSessionLogTaskRawResponse,
  StageDetailsRawData
} from './interfaces/raw-data';

import {
  JOB_OBJECT_STAGE_STATUS_DATA_MAP,
  JOB_OBJECT_STATUS_DATA_MAP,
  JOB_STATUS_DATA_MAP
} from './constants/job-stage';
import { doesStageHasFailures } from './utils';
import { JobAction } from './interfaces/job-action';
import { EntityActionsFactory } from '../entity-actions/factory/entityActionsFactory';
import { JOB_ACTIONS_DISPLAY_DETAILS } from './constants/job-actions';

export const JobFactory = (rawJob: JobRawData): Job => {
  return {
    mode: rawJob.type,
    jobId: rawJob.job_id,
    pipeline: rawJob.integration_details
      ? {
          id: rawJob.integration_id,
          seqId: rawJob.integration_details.seq_id,
          name: rawJob.integration_details.name,
          source: {
            logoURL: rawJob.integration_details.source_type_logo_url as string,
            darkModeLogoURL: rawJob.integration_details.source_type_logo_url as string,
            name: rawJob.integration_details.source_name,
            sourceType: rawJob.integration_details.source_type
          },
          destination: {
            logoURL: rawJob.integration_details.dest_type_logo_url,
            darkModeLogoURL: rawJob.integration_details.dest_type_logo_url,
            name: rawJob.integration_details.dest_name,
            destinationType: rawJob.integration_details.destination_type
          }
        }
      : undefined,
    startedAt: rawJob.created_at,
    duration: rawJob.duration,
    events: {
      ingested: rawJob.events_ingested,
      failed: rawJob.events_failed,
      loaded: rawJob.events_loaded,
      billable: rawJob.billable_events
    },
    status: JobStatusFactory(rawJob.status),
    actions: EntityActionsFactory<JobAction>(rawJob.job_actions ?? [], JOB_ACTIONS_DISPLAY_DETAILS),
    isDeletedIntegration: !!rawJob.integration_details?.is_deleted_integration,
    isSkipped: rawJob.status.value === JobStatus.SKIPPED,
    latency: {
      minLatency: rawJob.latency?.min_latency,
      meanLatency: rawJob.latency?.mean_latency,
      maxLatency: rawJob.latency?.max_latency
    },
    isLoadOnly: rawJob.job_mode === JobMode.LOAD_ONLY
  };
};

export const JobListWithCountFactory = (jobListRawData: JobListRawData): JobListWithCount => {
  return {
    total: jobListRawData.total_count,
    data: jobListRawData.data.map(JobFactory)
  };
};

export const PipelineJobListWithCountFactory = (
  jobListRawData: JobListRawData
): JobListWithCount => {
  return {
    total: jobListRawData.total_count,
    data: jobListRawData.data.map(JobFactory)
  };
};

export const JobDetailsFactory = (jobDetails: JobDetailsRawData): JobDetails => ({
  jobId: jobDetails.job_id,
  mode: jobDetails.type,
  status: JobStatusFactory(jobDetails.status),
  stats: {
    startedAt: jobDetails.created_at,
    duration: jobDetails.duration,
    objectsSuccess: jobDetails.objects_success,
    objectsQueued: jobDetails.objects_queued,
    objectsFailed: jobDetails.objects_failed,
    eventsIngested: jobDetails.events_ingested,
    eventsLoaded: jobDetails.events_loaded,
    eventsFailed: jobDetails.events_failed,
    billableEvents: jobDetails.billable_events,
    nonBillableEvents: jobDetails.non_billable_events,
    latency: {
      minLatency: jobDetails.latency.min_latency,
      meanLatency: jobDetails.latency.mean_latency,
      maxLatency: jobDetails.latency.max_latency
    }
  },
  actions: EntityActionsFactory<JobAction>(
    [
      ...jobDetails.actions,
      {
        type: JobAction.REFRESH,
        display_name: 'Refresh'
      }
    ],
    JOB_ACTIONS_DISPLAY_DETAILS
  ),
  isLoadOnly: jobDetails.job_mode === JobMode.LOAD_ONLY
});

export const JobObjectsWithCountFactory = (res: JobObjectsRawData): JobObjectListWithCount => {
  return {
    total: res.total_count,
    data: JobObjectListFactory(res.data)
  };
};

export const JobObjectFailuresFactory = (
  res: JobObjectFailuresRawData
): JobObjectFailuresWithCount => {
  return {
    total: res.total_count,
    data: JobObjectFailureFactory(res.data)
  };
};

export const JobFailuresFactory = (res: JobRawFailures): JobFailure[] => {
  return res.data.map(f => ({
    reason: f.reason,
    id: f.failure_id
  }));
};

export const JobSessionLogTaskResFactory = (
  res: JobSessionLogTaskRawResponse
): JobSessionLogTaskResponse => {
  return {
    status: res.status,
    preSignedURL: res.pre_signed_url,
    message: res.message
  };
};

const JobObjectFailureFactory = (data: JobObjectFailureRawData[]): JobObjectFailure[] => {
  return data.map((rawFailure: JobObjectFailureRawData) => {
    return {
      id: rawFailure.failure_id,
      failedTs: rawFailure.created_at,
      count: typeof rawFailure.failure_count === 'undefined' ? -1 : rawFailure.failure_count,
      reason: rawFailure.reason,
      actions: rawFailure.actions
    };
  });
};

export const JobObjectListFactory = (data: JobObjectRawData[]): JobObject[] =>
  data.map((rawObject: JobObjectRawData) => {
    const object: JobObject = {
      objectId: rawObject.object_id,
      objectName: rawObject.object_name,
      eventsIngested: rawObject.stages.ingest.events_input,
      startedAt: rawObject.started_at,
      duration: rawObject.duration,
      eventsLoaded: rawObject.stages.load.events_output,
      eventsFailed: rawObject.stages.ingest.events_failed + rawObject.stages.load.events_failed,
      status: JOB_OBJECT_STATUS_DATA_MAP[rawObject.status.value],
      latency: {
        minLatency: rawObject.latency.min_latency,
        meanLatency: rawObject.latency.mean_latency,
        maxLatency: rawObject.latency.max_latency
      },
      offset: rawObject.readable_offset,
      objectStages: [
        ObjectStageDetailsFactory(rawObject.stages.ingest, 'ingest'),
        ObjectStageDetailsFactory(rawObject.stages.load, 'load')
      ]
    };

    return object;
  });

const ObjectStageDetailsFactory = (data: StageDetailsRawData, stage: 'ingest' | 'load') => ({
  stage,
  startedAt: data.started_at,
  updatedAt: data.updated_at,
  inputEvents: data.events_input,
  outputEvents: data.events_output,
  failedEvents: data.events_failed,
  duration: data.duration,
  hasFailures: doesStageHasFailures(data.status.value),
  status: JOB_OBJECT_STAGE_STATUS_DATA_MAP[data.status.value]
});

export const JobStatusFactory = (rawStatus: GenericStatusRawData<JobStatus>) => {
  return JOB_STATUS_DATA_MAP[rawStatus.value as JobStatus];
};
